import { useRef, useState, useEffect, useCallback } from 'react';
import { Grid2 as Grid, Typography, Chip, useMediaQuery } from '@mui/material';
import InfoCard from '../../../shared/ui/InfoCard';
import { EventBus } from '@/shared/utils';
import { TeacherEvents } from '../events';
import { Teacher as TeacherDict } from '@/shared/i18n';
import { Course, TeacherItem } from '../types';
import CourseCard from '../../../shared/ui/course-card';
import theme from '@/shared/theme';
import { CourseType, SkilllevelsType } from 'src/shared/types/course';
import { LazyBackgroundImage } from 'src/shared/ui/lazy-background-image';

type TeacherDetailProps = {
  dict: TeacherDict;
  eventBus: EventBus<TeacherEvents>;
  teacher: TeacherItem;
  isSubscriber: boolean;
  mostViewedCourses?: Course[];
};

const TeacherDetailComponent = ({
  dict,
  eventBus,
  teacher,
  mostViewedCourses,
  isSubscriber,
}: TeacherDetailProps) => {
  const artistsCardRef = useRef<HTMLDivElement>(null);
  const [artistsCardHeight, setArtistsCardHeight] = useState(0);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  useEffect(() => {
    if (artistsCardRef.current) {
      setArtistsCardHeight(artistsCardRef.current.offsetHeight);
    }
  }, [teacher.artistsWorkedWith]);

  const bioMaxHeight = isMdUp ? 500 - artistsCardHeight - 16 : '';

  // filtriamo masterclass se non presentano playbackId dalla lista dei corsi più visti
  const adaptedMostViewedCourses = mostViewedCourses?.filter(
    (course) => course?.courseType?.toLowerCase() !== 'masterclass' || !!course?.playbackId,
  );

  const isCardLocked = useCallback(
    (courseType: CourseType | undefined, subscriber: boolean) => {
      return courseType?.toLowerCase() === 'masterclass' && !subscriber;
    },
    [isSubscriber],
  );
  return (
    <main className="space-y-8 md:space-y-10">
      <div>
        <div className="mb-2 md:mb-4">
          <Typography variant="h1" component="h1">
            {teacher.displayName}
          </Typography>
        </div>
        <Grid container className="w-full" spacing={{ xs: 1, md: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LazyBackgroundImage
              src={teacher.profilePictureUrl || ''}
              placeholder="/assets/images/placeholder/card-placeholder-2.png"
              className="rounded-xl h-[260px] md:min-h-[300px] md:h-full lg:h-[500px] w-full"
              style={{
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'top center',
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12, md: 6 }}
            display={'flex'}
            flexDirection={'column'}
            rowGap={{ xs: 1, md: 2 }}
          >
            <InfoCard className="h-full overflow-auto" style={{ maxHeight: `${bioMaxHeight}px` }}>
              <Typography variant="body-500">{teacher.biography}</Typography>
            </InfoCard>

            <div ref={artistsCardRef} className="flex flex-col gap-2 md:gap-4">
              <div className="flex flex-col sm:flex-row gap-2 md:gap-4">
                <InfoCard title={dict.teacher.instruments} className="w-full h-full flex-1">
                  <div className="flex flex-wrap gap-4">
                    {teacher.instruments.map((instrument, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <img src={instrument.iconUrl} className="size-8" alt={instrument.name} />
                        <Typography variant="body-500">{instrument.name}</Typography>
                      </div>
                    ))}
                  </div>
                </InfoCard>

                <InfoCard title={dict.teacher.teach_mainly} className="h-full w-auto">
                  <Typography variant="body-500">{teacher.mainSubject}</Typography>
                </InfoCard>
              </div>

              {!!teacher?.artistsWorkedWith?.artists?.length && (
                <InfoCard title={dict.teacher.artists_played_with}>
                  <div className="flex gap-2">
                    {teacher?.artistsWorkedWith?.artists?.map((artist, index) => (
                      <Chip
                        key={index}
                        label={
                          <Typography variant="button-s">{`${artist?.name || ''} ${artist?.surname || ''}`}</Typography>
                        }
                        sx={{ backgroundColor: theme.palette.neutral.main }}
                      />
                    ))}
                  </div>
                </InfoCard>
              )}
            </div>
          </Grid>
        </Grid>
      </div>
      {!!adaptedMostViewedCourses && adaptedMostViewedCourses?.length > 0 && (
        <div className="flex flex-col justify-center text-center gap-4">
          <Typography component="h3" variant="h3" className="text-left sm:text-center">
            I più visti
          </Typography>
          <div className="flex flex-nowrap justify-start sm:flex-wrap sm:justify-center gap-4 overflow-x-auto">
            {adaptedMostViewedCourses?.map((el) => {
              return (
                <CourseCard
                  eventBus={eventBus}
                  documentId={el.documentId}
                  cardClassName="flex-none w-[80%] max-w-[318px] sm:w-[318px]"
                  highlighted={el.highlighted}
                  key={el.id}
                  title={el.title}
                  level={el.level?.toLowerCase() as SkilllevelsType}
                  genres={el.genres}
                  instructors={el.teachers.map((el: any) => ({
                    avatarUrl: el.profilePicture?.url || el?.avatarUrl || '',
                    name: el.displayName,
                  }))}
                  imageSrc={el.courseImageUrl || '/assets/images/card-placeholder.png'}
                  videoCount={el.videoCount}
                  coursePercentage={el.coursePercentage ?? 0}
                  categoryTags={el.categoryTags}
                  courseType={el.courseType}
                  isLocked={el.isLocked || isCardLocked(el.courseType, isSubscriber) || false}
                />
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="flex flex-col items-center sm:flex-row gap-2 mb-4 text-center sm:text-left sm:justify-between">
          <Typography variant="h3" component="p" className="w-full">
            {dict.teacher.all_courses}
          </Typography>
          <Typography variant="s1" className="flex gap-x-1">
            {teacher.allCourses.length} <span>{dict.teacher.items}</span>
          </Typography>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {teacher.allCourses.map(
            (
              course: any, // Replace any with correct type api/teacher/types -> interface CourseData
            ) => (
              <CourseCard
                {...course}
                eventBus={eventBus}
                documentId={course.documentId}
                cardClassName="w-full max-w-[318px] sm:max-w-full mx-auto"
                key={course.id}
                title={course.title}
                level={course.level?.toLowerCase() as SkilllevelsType}
                genres={course.genres}
                instructors={course.teachers.map((el: any) => ({
                  avatarUrl: el.profilePicture?.url || '',
                  name: el.displayName,
                }))}
                imageSrc={course.courseImageUrl || '/assets/images/card-placeholder.png'}
                videoCount={course.videoCount}
                coursePercentage={course.coursePercentage ?? 0}
                categoryTags={[]}
                courseType={course.courseType}
                isLocked={course.isLocked || isCardLocked(course.courseType, isSubscriber) || false}
              />
            ),
          )}
        </div>
      </div>
    </main>
  );
};

export default TeacherDetailComponent;
